import { CircularProgress } from '@mui/material'
import React, { type FC, useEffect, useMemo } from 'react'
import { Send } from '@mui/icons-material'
import { isResolved, usePromise } from 'react-sync-promise'

import {
    Capability,
    ConnectwareError,
    createIsAuthenticatedWithCapabilitiesSelector,
    type Metrics as MetricsModel,
    NotificationSeverity,
    Translation,
} from '../../../../domain'

import { useAsyncCallback } from '../../Callback'
import { Button } from '../../common'
import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { useAppState, useAppUsecases } from '../../State'

const selectCanManageMetrics = createIsAuthenticatedWithCapabilitiesSelector(Capability.SYSTEM_METRICS_MANAGE)

export const SendMetricsToPortalButton: FC = () => {
    const isAuthenticated = useAppState(selectCanManageMetrics)
    const { systemMetricsUsecase, toastUsecase } = useAppUsecases()
    const translator = useTranslator()
    const [sendMetricsToPortal, isLoading, result] = useAsyncCallback(() => systemMetricsUsecase.sendMetricsToPortal(), [systemMetricsUsecase])
    const sentResponse = isLoading || result === null ? false : result

    const metrics = usePromise<MetricsModel, ConnectwareError>(useMemo(() => systemMetricsUsecase.fetchMetrics(), [systemMetricsUsecase]))

    useEffect(() => {
        if (sentResponse === false) {
            /** Nothing has happened yet */
            return
        }

        toastUsecase.show({
            severity: ConnectwareError.is(result) ? NotificationSeverity.ERROR : NotificationSeverity.SUCCESS,
            title: ConnectwareError.is(result) ? result.message : translator.formatTranslation(Translation.SUCCESSFULLY_SENT_METRICS_TO_PORTAL),
        })
    }, [sentResponse, toastUsecase, translator])

    if (isResolved(metrics) && !metrics.value.isPortalReachable) {
        return null
    }

    return (
        <Button
            data-testid="send-metrics-button"
            variant="secondary"
            size="large"
            onClick={sendMetricsToPortal}
            disabled={!isAuthenticated || isLoading}
            startIcon={isLoading ? <CircularProgress size="1rem" /> : <Send />}
        >
            <FormattedTranslation id={Translation.SEND_METRICS_TO_PORTAL} />
        </Button>
    )
}
